import Sortable from 'sortablejs';
import { apiFetch } from './helpers';

const WIDGET_KEYS = ['weather', 'bus', 'train', 'markets', 'electricity', 'news', 'history', 'hockey', 'aibubble', 'festivals', 'eurojackpot', 'proverb'] as const;
type WidgetKey = typeof WIDGET_KEYS[number];

const WIDGET_SELECTORS: Record<WidgetKey, string> = {
    weather: '.weather-widget',
    bus: '.bus-widget',
    train: '.train-widget',
    markets: '.markets-widget',
    electricity: '.electricity-widget',
    news: '.news-widget',
    history: '.history-widget',
    hockey: '.hockey-widget',
    aibubble: '.aibubble-widget',
    festivals: '.festivals-widget',
    eurojackpot: '.eurojackpot-widget',
    proverb: '.proverb-widget',
};

const WIDGET_DEFAULT_HIDDEN: ReadonlySet<WidgetKey> = new Set(['proverb']);

export function isWidgetVisible(key: string): boolean {
    const stored = localStorage.getItem(`widget-${key}`);
    if (stored !== null) return stored === 'true';
    return !WIDGET_DEFAULT_HIDDEN.has(key as WidgetKey);
}

export function loadWidgetVisibility(): void {
    const oldOil = localStorage.getItem('widget-oil');
    if (oldOil !== null && localStorage.getItem('widget-markets') === null) {
        localStorage.setItem('widget-markets', oldOil);
        localStorage.removeItem('widget-oil');
    }
    const storedOrder = localStorage.getItem('widget-order');
    if (storedOrder && storedOrder.includes('"oil"')) {
        localStorage.setItem('widget-order', storedOrder.replace('"oil"', '"markets"'));
    }

    for (const key of WIDGET_KEYS) {
        const stored = localStorage.getItem(`widget-${key}`);
        const defaultVisible = !WIDGET_DEFAULT_HIDDEN.has(key);
        const visible = stored === null ? defaultVisible : stored === 'true';
        const widget = document.querySelector(WIDGET_SELECTORS[key]) as HTMLElement | null;
        const toggle = document.querySelector(`input[data-widget="${key}"]`) as HTMLInputElement | null;
        if (widget) widget.style.display = visible ? '' : 'none';
        if (toggle) toggle.checked = visible;
    }
}

export function initWidgetToggles(): void {
    const dropdown = document.getElementById('settings-dropdown');
    const btn = document.getElementById('settings-btn');
    if (!dropdown || !btn) return;

    btn.addEventListener('click', (e: Event) => {
        e.stopPropagation();
        dropdown.classList.toggle('open');
    });

    document.addEventListener('click', () => {
        dropdown.classList.remove('open');
    });

    dropdown.addEventListener('click', (e: Event) => {
        e.stopPropagation();
    });

    dropdown.addEventListener('change', (e: Event) => {
        const target = e.target as HTMLInputElement;
        const key = target.dataset['widget'] as WidgetKey | undefined;
        if (!key || !WIDGET_KEYS.includes(key)) return;

        const visible = target.checked;
        localStorage.setItem(`widget-${key}`, String(visible));
        const widget = document.querySelector(WIDGET_SELECTORS[key]) as HTMLElement | null;
        if (widget) widget.style.display = visible ? '' : 'none';
    });
}

// --- Widget reorder (SortableJS) ---

function getWidgetOrder(): string[] {
    const stored = localStorage.getItem('widget-order');
    if (stored) {
        try {
            const order = JSON.parse(stored) as string[];
            if (Array.isArray(order) && order.length > 0) return order;
        } catch { /* ignore */ }
    }
    return [...WIDGET_KEYS];
}

function saveWidgetOrder(order: string[]): void {
    localStorage.setItem('widget-order', JSON.stringify(order));
}

export function applyWidgetOrder(): void {
    const container = document.querySelector('.container') as HTMLElement | null;
    if (!container) return;
    const order = getWidgetOrder();

    const widgets = new Map<string, HTMLElement>();
    for (const key of WIDGET_KEYS) {
        const el = container.querySelector(`[data-widget-key="${key}"]`) as HTMLElement | null;
        if (el) widgets.set(key, el);
    }

    for (const key of order) {
        const el = widgets.get(key);
        if (el) container.appendChild(el);
    }
    for (const key of WIDGET_KEYS) {
        if (!order.includes(key)) {
            const el = widgets.get(key);
            if (el) container.appendChild(el);
        }
    }
}

function getCurrentWidgetOrder(container: HTMLElement): string[] {
    return Array.from(container.querySelectorAll<HTMLElement>('[data-widget-key]'))
        .map(el => el.dataset['widgetKey'] || '')
        .filter(k => k !== '');
}

export function initReorderMode(): void {
    const btn = document.getElementById('reorder-btn');
    const container = document.querySelector('.container') as HTMLElement | null;
    const actions = document.getElementById('reorder-actions');
    const okBtn = document.getElementById('reorder-ok-btn');
    const cancelBtn = document.getElementById('reorder-cancel-btn');
    if (!btn || !container || !actions || !okBtn || !cancelBtn) return;

    let reorderMode = false;
    let preReorderOrder: string[] | null = null;

    const sortable = Sortable.create(container, {
        draggable: '[data-widget-key]',
        animation: 150,
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        dragClass: 'sortable-drag',
        disabled: true,
        dataIdAttr: 'data-widget-key',
        forceFallback: true,
        fallbackClass: 'sortable-fallback',
        fallbackTolerance: 3,
    });

    function enterReorderMode(): void {
        reorderMode = true;
        preReorderOrder = getCurrentWidgetOrder(container!);
        btn!.classList.add('active');
        container!.classList.add('reorder-mode');
        document.body.classList.add('reorder-active');
        actions!.classList.add('visible');
        sortable.option('disabled', false);
    }

    function exitReorderMode(): void {
        reorderMode = false;
        preReorderOrder = null;
        btn!.classList.remove('active');
        container!.classList.remove('reorder-mode');
        document.body.classList.remove('reorder-active');
        actions!.classList.remove('visible');
        sortable.option('disabled', true);
    }

    btn.addEventListener('click', () => {
        if (!reorderMode) {
            enterReorderMode();
        } else {
            saveWidgetOrder(sortable.toArray());
            exitReorderMode();
        }
    });

    okBtn.addEventListener('click', () => {
        saveWidgetOrder(sortable.toArray());
        exitReorderMode();
    });

    cancelBtn.addEventListener('click', () => {
        if (preReorderOrder && container) {
            // Directly restore DOM order — sortable.sort() is unreliable mid-drag
            const widgets = new Map<string, HTMLElement>();
            container.querySelectorAll<HTMLElement>('[data-widget-key]').forEach(el => {
                widgets.set(el.dataset['widgetKey'] || '', el);
            });
            for (const key of preReorderOrder) {
                const el = widgets.get(key);
                if (el) container.appendChild(el);
            }
        }
        exitReorderMode();
    });
}

// --- Version check ---

let currentVersion: string | null = null;

export async function checkForNewVersion(): Promise<void> {
    try {
        const response = await apiFetch('version.json?_=' + Date.now());
        if (!response.ok) return;
        const data = await response.json();
        const latest = data.version as string;
        if (!latest) return;

        if (currentVersion === null) {
            currentVersion = latest;
            console.log(`DailyDashboard v${currentVersion}`);
            const versionEl = document.getElementById('dev-version');
            if (versionEl) versionEl.textContent = `v${currentVersion}`;
            return;
        }

        if (latest !== currentVersion) {
            const toast = document.getElementById('update-toast');
            if (toast) toast.classList.add('visible');
        }
    } catch {
        // Silently ignore version check failures
    }
}

export function initUpdateToast(): void {
    const btn = document.getElementById('update-toast-btn');
    if (btn) btn.addEventListener('click', () => location.reload());
}
